import { BaseResponse } from "@/modules/common/dtos";
import { BaseDTO, User } from "@/modules/common/dtos/index.v2";
import { FunnelWithStatus } from "@/modules/funnels/dtos";
import { LeadTypesDefaultUsersV2 } from "@/modules/funnels/dtos/index.v2";
import { Lead } from "@/modules/leads/types";
import { Property } from "@/modules/lead-page/new/components/recommendations/recommendations";
import { UUID } from "crypto";
import { FollowUpStatusEnum } from "@/modules/lead-page/enums";

type Domain = {
  id: number;
  name: string;
  userPoolId: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ViewerList = {
  id: number;
  uid: string;
  name: string;
  preferredUsername: string | null;
  email: string | null;
  phoneNumber: string | null;
  banned: boolean;
  jobId: number | null;
  domainId: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  domain: Domain;
  job: any | null;
  customGroups: any[];
  identityProviders: any[];
};

export type GetViewerListResponse = {
  page: number;
  pageSize: number;
  total: number;
  results: ViewerList[];
};
export type Journey = {
  uid: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  dateOfLastContact: string;
};

export type Viewer = {
  uid: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  journey: Journey;
  user: User;
};

export type GetViewerResponse = Viewer[];

export type Funnel = {
  uid: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  name: string;
};

export interface GetLeadTypeResponseV2 extends BaseResponse {
  data: LeadTypeResponse;
}

export type LeadTypeResponse = {
  page: number;
  pageSize: number;
  total: number;
  results: LeadType[];
};

export interface LeadType extends BaseDTO {
  name: string;
  funnel: FunnelWithStatus;
  allowUpdate?: boolean;
  allowReferrals?: boolean;
  defaultUsers?: LeadTypesDefaultUsersV2[];
  form: LeadTypeForm | null;
  acceptListing?: boolean;
}

export interface LeadTypeFormResponse extends BaseDTO {}

export interface LeadTypeForm extends BaseDTO {
  name: string;
}

type MergeJourneysDto = {
  primaryJourneyUId: string;
  secondaryJourneyUId: string;
};

type MergeLeadsDto = {
  leadTypeUId: string;
  methodOfContact: string;
  location: string;
  referral?: string;
  mergeJourneysDto: MergeJourneysDto[];
};

export interface MergeCustomerDtoRequest {
  secondaryCustomerUId: string;
  name: string;
  mergeLeadsDto: MergeLeadsDto[] | [];
}

export interface LeadHistoryItem {
  _id: string;
  table: string;
  entityUId: string;
  system: string;
  method: string;
  values: Record<string, string | number | boolean | null>;
  oldValues: Record<string, string | number | boolean | null>;
  valueChanges: Record<
    string,
    {
      old: string | number | null;
      new: string | number | null;
    }
  >;
  incomingUserId: string;
  actor: any;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface UpdateLeadTypeRequestV2 {
  leadTypeUId: string;
  newUserUId?: string;
  autoAssign?: boolean;
  referralCommissionApplies?: boolean;
  keepCurrentLead?: boolean;
  referralNumberOfUnits?: string;
  referralBudget?: string;
  referrerUId?: string;
  propertyTypeUId?: string;
}

export interface updateSalesRequest {
  actualValue?: number;
  areaUId?: string;
  compoundUId?: string;
  cashPrice?: number;
  blockUpdateDate?: string;
  dateOfBlocked?: string;
  dateOfContraction?: string;
  actualDateOfContraction?: string;
  dateOfDownpayment?: string;
  dateOfEOI?: string;
  dateOfReservation?: string;
  downPayment?: number;
  expectedContractionDate?: number;
  expectedValue?: number;
  nameOnContract?: string;
  propertyTypeUId?: string;
  saleCategory?: string;
  unitLocation?: string;
  unitNumber?: string;
  unitValue?: number;
  yearsOfPayment?: number;
  saleTypeUId?: string;
  comment?: string;
  organizationUId?: string;
}

export interface CreateSaleResponse {
  saleCategory?: string;
  comment?: string;
  unitLocation?: string;
  nameOnContract?: string;
  cashPrice?: number;
  downPayment?: number;
  yearsOfPayment?: number;
  areaUId?: string;
  compoundUId?: string;
  propertyTypeUId?: string;
  saleTypeUId?: string;
  userUId?: string;
  dateOfDownPayment?: string;
  dateOfContraction?: string;
  actualDateOfContraction?: string;
  expectedContractionDate?: string;
  dateOfEOI?: string;
  expectedValue?: number;
  dateOfBlocked?: string;
  unitValue?: number;
  unitValueCurrency?: string;
  unitNumber?: string;
  dateOfReservation?: string;
  journeyUId?: string;
  organizationUId?: string;
}

export interface PaymentAndCollectionStatusUpdate {
  unitNumber?: string;
  saleId?: number;
  newStatusId?: number;
  oldStatusId?: number;
  newStatusName?: string;
  oldStatusName?: string;
  newPaymentStatuses?: string[];
  oldPaymentStatuses?: string[];
  newCollectionStatuses?: string[];
  oldCollectionStatuses?: string[];
  collectionCreationDates?: string[];
  paymentCreationDates?: string[];
  createdAt?: string;
}

export interface AddReferralRequest {
  referralNumberOfUnits?: string;
  referralBudget?: string;
  referrerUId?: string;
  propertyTypeUId?: string;
  comment?: string;
}

export interface Group {
  type: LeadType;
  parent?: Lead;
  child?: Lead;
}

export enum ActivityType {
  Comment = "comment",
  StatusUpdate = "status_update",
  AssignmentUpdate = "assignment_update",
  OwnersComment = "owners",
  PropertyTrackComment = "property_track",
  InterestUpdate = "interest_update",
  Rating = "rating",
  AIChatbot = "ai_comment",
  ConversationSummary = "conversation_summary",
  RespondIOSessionSummary = "respond_io_session_summary",
  PropertyPriceUpdate = "property_price_update",
  LeadMiningComment = "lead_mining_comment",
  ZoomCall = "zoom_call",
  WhatsappConversation = "whatsapp_conversation",
}

export interface MentionedUser {
  uid: string;
  name: string;
  color: string | null;
  rank: string | null;
  email: string | null;
  phoneNumber: string | null;
}

export enum ASSIGNMENT_TYPE {
  MANUAL = "manual",
  MANUAL_MULTI_SELECTION = "manual_multiSelection",
  WITH_MANUAL_MULTI_SELECTION = "with manual Multi-Selection",
  AUTO_REASSIGNMENT = "auto_reassignment",
  REASSIGN_TO_MANAGER = "through re-assign to manager button",
  AUTOMATIC_AUTO_ASSIGNMENT = "Automatically due to Auto Assignment trigger",
  BOT_AUTOASSIGNMENT = "Automatically due to Bot Auto Assignment trigger",
  FROM_POOL = "from pool",
  AUTO_ASSIGNMENT = "auto_assignment",
  POOL = "pool",
  DELEGATION = "Delegation",
  AUTOMATICALLY = "Automatically",
  MANUALLY = "Manually",
  ROTATION = "Rotation",
  AUTO_ASSIGNMENT_BUTTON = "auto_assignment_button",
  BULK_UPLOAD = "through bulk upload",
  SINGLE_LEAD = "single lead",
  LIST_ASSIGNMENT = "list_assignment",
  SHARES = "Reassigned automatically by Shares",
}

export interface ActivityDeveloperTrack {
  trackUId?: string;
  developerName?: string;
  developerUserName?: string;
  developerLogoPath?: string;
  showLogo?: boolean;
}

export interface ActivityFormData extends Record<string, any> {
  developerTracks?: ActivityDeveloperTrack[];
  showLogo?: boolean;
  field_label?: string;
  fieldLabel?: string;
  submitted_data?: any;
  submittedData?: any;
  type?: string;

}

export interface AttachmentMetadataResponse {
  duration?: number;
}

export interface ActivityAttachment {
  uid: string;
  mediaUrl: string;
  mediaType: string;
  presignedUrl: string;
  metadata?: AttachmentMetadataResponse;
}

export interface ActivityItem {
  type: ActivityType;
  id: number;
  uid: UUID;
  zoomCallUid?: string | null;
  zoomCallSummary?: string[] | null;
  zoomCallSentiment?: Record<string, any> | null;
  zoomCallFlags?: Record<string, any> | null;
  zoomCallSuggestedStatusName?: string | null;
  zoomCallSuggestedStatusColor?: string | null;
  zoomCallSuggestedStatusId?: number | null;
  zoomCallOldStatusName?: string | null;
  zoomCallOldStatusColor?: string | null;
  zoomCallAiStatus?: "pending" | "processing" | "completed" | "failed" | null;
  currentJourneyId: number;
  oldJourneyId: number | null;
  oldJourneyNanoId: string | null;
  content: string | null;
  actorName: string | null;
  actorColor: string | null;
  previousUser: string | null;
  previousUserColor: string | null;
  newUser: string | null;
  newUserColor: string | null;
  previousStatus: string | null;
  previousStatusColor: string | null;
  newStatus: string | null;
  newStatusColor: string | null;
  assignmentType: ASSIGNMENT_TYPE | null;
  mentions: MentionedUser[];
  createdAt: string;
  deletedAt: string | null;
  formData: ActivityFormData | null;
  entityType?: "Lead" | "Track";
  rating?: number;
  followUpStatus?: string | null;
  attachments?: ActivityAttachment[];
  triggeredBy?: string | null;
}

export interface ActivityResponseDto {
  data: {
    items: ActivityItem[];
    count: number;
    nextCursor: string;
    prevCursor?: string;
    hasNext?: boolean;
    hasPrev?: boolean;
    hasChild?: boolean;
  };
}

export interface NawyNowRecommendationsResponseDto {
  data: Property[];
}

export interface GetLeadRatingsFiltersDto {
  to?: string;
  from?: string;
  userUIds?: string;
  ratings?: number[];
  statusNames?: string;
  journeyUIds?: string;

  page?: number;
  pageSize?: number;

  search?: string;
  sort?: string;
}

export interface AddRatingCommentDto {
  comment: string;
}

export interface ResolveRatingDto {
  comment?: string;
}

export interface RatingUser {
  id?: number;
  uid?: string;
  name?: string;
  color?: string;
  rank?: {
    title?: string;
  };
}

export interface RatingComment {
  id?: number;
  uid?: string;
  comment: string;
  createdAt: string | Date;
  user?: RatingUser | null;
}

export interface RatingDetailsResponse {
  uid?: string;
  rating?: number;
  comment?: string;
  status?: {
    name?: string;
  };
  followUpStatus?: FollowUpStatusEnum;
  createdAt?: string | Date;
  agent?: RatingUser | null;
  comments?: RatingComment[];
}

export enum WhatsappSenderRole {
  Agent = "agent",
  Customer = "customer",
}

export interface WhatsappMessage {
  id: number;
  time: string;
  senderName: string;
  senderRole: WhatsappSenderRole;
  message: string;
}

export type WhatsappConversationResponse = WhatsappMessage[];

export interface ZoomCallDetailResponse {
  uid: string;
  callId: string;
  telephone: string;
  agentEmail: string;
  agentUid: string;
  agentName: string | null;
  agentColor: string | null;
  status: string;
  callAnsweredAt: string | null;
  durationSeconds: number | null;
  presignedRecordingUrl: string | null;
  presignedTranscriptionUrl: string | null;
  summary: string[] | null;
  buyingPotential: Record<string, any> | null;
  sentiment: Record<string, any> | null;
  nextActions: Record<string, any> | null;
  flags: Record<string, any> | null;
  extractedFields: Record<string, any> | null;
  customer: { uid: string; name: string } | null;
  journey: { id: number; uid: string; nanoId: string } | null;
  createdAt: string;
  updatedAt: string;
  history: Array<{
    uid: string;
    status: string;
    recordingUid: string | null;
    transcriptionUid: string | null;
    createdAt: string;
  }>;
  canViewAnalysis: boolean;
  canViewTranscription: boolean;
  canViewRecording: boolean;
  aiStatus?: {
    status: "pending" | "processing" | "completed" | "failed" | null;
    suggestedStatusId: number | null;
    suggestedStatusName: string | null;
    suggestedStatusColor: string | null;
    oldStatusId: number | null;
    oldStatusName: string | null;
    oldStatusColor: string | null;
    generatedAt: string | null;
  } | null;
}
